/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import Header from 'components/Header/Header';
import { RouterUrlEnum } from 'enums/routerUrl';
import { createForm, FormApi, Unsubscribe } from 'final-form';
import { Component } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { AppDispatch, AppState } from 'store';
import { login } from 'store/auth/auth.actions';
import { clearStoreError } from 'store/error/error.actions';
import { IErrorState } from 'store/error/error.interface';
import { getErrorsSelector } from 'store/error/error.selectors';
import { getAuthSpinnerSelector } from 'store/spinner/spinner.selectors';
import { composeValidators, email, fetchValidatorObject, required } from 'utils/customValidators';
import { getEnvironment } from 'utils/handlers/envHandler';
import { UserAuthenticationRequest } from 'utils/restApplicationClientTypeOverrides';
import CustomField from '../Input/CustomField';
import {
  CursorPointer,
  LoginFormContainer,
  LoginFormErrorTypography,
  LoginFormLink,
  LoginFormLinkDiv,
  LoginFormPageContainer,
  LoginFormTopBar,
} from './LoginForm.styled';

interface IFormValues {
  email: string;
  password: string;
  clientName: string;
}

interface IState {
  submitValues: IFormValues;
}

interface IDispatchProps {
  login: (payload: UserAuthenticationRequest) => AppDispatch;
  clearAuthErrors: (payload: string) => AppDispatch;
}

interface IStateProps {
  spinner: boolean;
  authErrors: IErrorState['errors'];
}

type PropType = IDispatchProps & IStateProps & WithTranslation;

export class LoginForm extends Component<PropType, IState> {
  unsubscribe: Unsubscribe;
  constructor(props: PropType) {
    super(props);
    const { clearAuthErrors } = this.props;

    this.state = {
      submitValues: {
        clientName: '',
        email: '',
        password: '',
      },
    };

    this.form = createForm({
      onSubmit: this.handleSubmit,
      destroyOnUnregister: true,
    });
    this.unsubscribe = this.form.subscribe(
      () => {
        if (this.props.authErrors['login']) {
          clearAuthErrors('login');
        }
      },
      { values: true },
    );
  }

  componentWillUnmount(): void {
    this.unsubscribe();
  }

  form: FormApi<IFormValues, Partial<Record<string, unknown>>>;

  handleSubmit = ({ clientName, email, password }: IFormValues): void => {
    this.props.login({ clientName, email, password });
  };

  render(): JSX.Element {
    const { t, authErrors, spinner } = this.props;
    const { submitValues } = this.state;

    return (
      <LoginFormPageContainer>
        <Header />
        <Box display="flex" alignItems="center" marginTop="144px" flexDirection="column">
          <LoginFormContainer>
            <LoginFormTopBar>
              <Typography color="textSecondary" component="div">
                <Box fontWeight="fontWeightBold" color="#748392">
                  {t('loginForm.logIn')}
                </Box>
              </Typography>
            </LoginFormTopBar>
            <Box padding="12px 24px">
              <FinalForm
                onSubmit={this.handleSubmit}
                form={this.form}
                validate={fetchValidatorObject(authErrors['login'], submitValues as unknown as Record<string, string>)}
                subscription={{ submitting: true, pristine: true }}
                render={({ handleSubmit, submitting }): JSX.Element => (
                  <form onSubmit={handleSubmit}>
                    {authErrors['login'] && (
                      <LoginFormErrorTypography color="error">{t(authErrors['login'])}</LoginFormErrorTypography>
                    )}
                    <Field
                      name="clientName"
                      component={CustomField}
                      validate={required}
                      label="loginForm.labels.clientName"
                      format={(value): string => (value === undefined ? '' : value.trim())}
                    />

                    <Field
                      name="email"
                      component={CustomField}
                      validate={composeValidators([required, email])}
                      label="loginForm.labels.email"
                      format={(value): string => (value === undefined ? '' : value.trim())}
                    />
                    <Field
                      name="password"
                      component={CustomField}
                      validate={composeValidators([required])}
                      textFieldProps={{ type: 'password' }}
                      label="loginForm.labels.password"
                    />
                    <LoginFormLinkDiv>
                      <LoginFormLink to={RouterUrlEnum.FORGOT_PASSWORD} css={CursorPointer}>
                        <Box fontWeight="fontWeightBold">{t('common.forgotPassword')}</Box>
                      </LoginFormLink>
                    </LoginFormLinkDiv>
                    <Box marginTop="16px">
                      <Typography variant="subtitle2" color="textPrimary" component="div">
                        {t('loginForm.XTM.credentials')}
                        <br />
                        {t('loginForm.XTM.signUp1')}
                        <a css={CursorPointer} href={getEnvironment('REACT_APP_REGISTER_URL') || ''}>
                          {t('loginForm.XTM.signUp2')}
                        </a>
                        {t('loginForm.XTM.signUp3')}
                        <br />
                        {t('loginForm.XTM.contact1')}
                        <a css={CursorPointer} href={getEnvironment('REACT_APP_SALES_TEAM_EMAIL') || ''}>
                          {t('loginForm.XTM.contact2')}
                        </a>
                        {t('loginForm.XTM.contact3')}
                      </Typography>
                    </Box>
                    <Box marginTop="18px">
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        type="submit"
                        disabled={submitting || spinner || Boolean(authErrors['login'])}
                      >
                        {spinner && <CircularProgress size={24} color="secondary" />}
                        {!spinner && t('loginForm.logIn')}
                      </Button>
                    </Box>
                  </form>
                )}
              />
            </Box>
          </LoginFormContainer>
        </Box>
      </LoginFormPageContainer>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  login: (payload: UserAuthenticationRequest): AppDispatch => dispatch(login(payload)),
  clearAuthErrors: (payload: string): AppDispatch => dispatch(clearStoreError(payload)),
});

const mapStateToProps = (state: AppState): IStateProps => ({
  authErrors: getErrorsSelector(state),
  spinner: getAuthSpinnerSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LoginForm));
